import { BoundForm, Css, boundRadioGroupField, boundSelectCardGroupField } from "@homebound/beam";
import { useMemo } from "react";
import { ChangeEventReasonsBoundSelectField } from "src/components/autoPopulateSelects/ChangeEventReasonsBoundSelectField";
import { ChangeEventType, ChangeEventWorkflowDetail } from "src/generated/graphqlTypes";
import { useDefineChangeStepQuery } from "src/routes/projects/change-events/guided/components/1.DefineChangeStep.generated";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { queryResult } from "src/utils";
import { BaseFormState } from "../workflows/types";

type DefineChangeStepProps = { formState: BaseFormState };
export function DefineChangeStep(props: DefineChangeStepProps) {
  const { formState } = props;
  const query = useDefineChangeStepQuery({ fetchPolicy: "cache-first" });
  return (
    <div css={Css.df.fdc.gap3.$}>
      <div>
        <h2 css={Css.xl.$}>Describe Change</h2>
        <p css={Css.sm.gray700.$}>Tell us what type of change this is.</p>
      </div>
      {queryResult(query, ({ enumDetails }) => (
        <DefineChangeStepView formState={formState} workflows={enumDetails.changeEventWorkflow} />
      ))}
    </div>
  );
}

type DefineChangeStepViewProps = Pick<DefineChangeStepProps, "formState"> & { workflows: ChangeEventWorkflowDetail[] };
function DefineChangeStepView(props: DefineChangeStepViewProps) {
  const project = useProjectContext();
  const { formState, workflows } = props;
  const workflowOptions = useMemo(
    () => workflows.map((w) => ({ value: w.code, label: w.name, description: w.description })),
    [workflows],
  );
  return (
    <BoundForm
      formState={formState}
      rows={[
        {
          workflow: boundSelectCardGroupField({
            view: "list",
            options: workflowOptions,
            onChange: (value) => {
              // Reset the reason id when the workflow changes
              formState.reasonId.set(undefined);
              formState.workflow.set(value);
            },
          }),
        },
        {
          reactNodeReason: (
            <ChangeEventReasonsBoundSelectField
              field={formState.reasonId}
              label="Reason"
              workflow={formState.workflow.value ?? undefined}
              disabled={!formState.workflow.value}
            />
          ),
        },
        {
          type: boundRadioGroupField({
            label: `Chargeable to ${project.clientNoun}`,
            layout: "horizontal",
            options: [
              { value: ChangeEventType.Internal, label: "No" },
              {
                value: ChangeEventType.External,
                label: "Yes",
                // Certain projects have an agreement where every cost after completion is HTI's cost,
                // so they can't create chargeable change events post-completion.
                disabled:
                  project.shouldAccrueAsComplete &&
                  `This project has reached investor closeout. Changes cannot be made to the ${project.clientNoun} price.`,
              },
            ],
            helperText: `Determines whether this change is priced and billed to the ${project.clientNoun}. 'Yes' will capture a ${project.clientNoun}
              price alongside cost, and route for signature before it's final. 'No' will be treated as an internal
              change. No ${project.clientNoun} price is captured, and no signature is required.`,
          }),
        },
      ]}
    />
  );
}
